/**
 * Delta change entry for tracking mutations.
 */
interface DeltaChange {
    type: 'app' | 'flow' | 'meta';
    action: 'update' | 'create' | 'delete';
    id?: string;
    fields?: string[];
}

export class Flow {
    public _data: any = {};
    public meta: any = {};

    constructor(private spec: Spec, flow_id: any) {
        if (!spec.data.flow[flow_id]) return;
        this._data = spec.data.flow[flow_id];
        this._data.id = flow_id;
    }

    public raw() {
        return this._data;
    }

    public id() {
        return this._data.id;
    }

    public title() {
        if (this._data.title)
            return this._data.title;
        return this.app().title();
    }

    public data() {
        return this._data.data;
    }

    public active() {
        return this._data.active;
    }

    public inputs() {
        return this._data.inputs;
    }

    public outputs() {
        return this._data.outputs;
    }

    public width() {
        let w = this._data.width ? this._data.width : 260;
        if (w * 1 < 260) return 260;
        return w * 1;
    }

    public app() {
        try {
            let app_id = this._data.app_id;
            return this.spec.app(app_id);
        } catch (e) {
        }
        return null;
    }

    public set(data: any = {}) {
        const changedKeys = Object.keys(data);
        for (let key in data) {
            this._data[key] = data[key];
        }
        this.spec.trackChange('flow', 'update', this._data.id, changedKeys);
    }
}

export class App {
    public _data: any = {};

    constructor(private spec: Spec, app_id: any) {
        if (!spec.data.apps[app_id]) return;
        this._data = spec.data.apps[app_id];
        this._data.id = app_id;
    }

    public raw() {
        return this._data;
    }

    public id() {
        return this._data.id;
    }

    public title() {
        if (this._data.title)
            return this._data.title;
        return this.app().title();
    }

    public version() {
        return this._data.version;
    }

    public inputs() {
        return this._data.inputs;
    }

    public outputs() {
        return this._data.outputs;
    }

    /**
     * Update app fields and track the change.
     */
    public set(data: any = {}) {
        const changedKeys = Object.keys(data);
        for (let key in data) {
            this._data[key] = data[key];
        }
        this.spec.trackChange('app', 'update', this._data.id, changedKeys);
    }
}

export class Spec {
    private _pendingChanges: DeltaChange[] = [];
    private _snapshot: string = '';
    private _undoStack: string[] = [];
    private _redoStack: string[] = [];
    private _isUndoRedo: boolean = false;
    private _undoDebounceTimer: any = null;
    private static readonly MAX_HISTORY = 50;
    private static readonly UNDO_BATCH_DELAY = 1000;
    public onChange: (() => void) | null = null;

    constructor(public data: any) {
        this._snapshot = JSON.stringify(this.data);
    }

    /**
     * Take a snapshot of the current data state.
     * Called after successful save/patch to mark the "clean" state.
     */
    public takeSnapshot() {
        this._snapshot = JSON.stringify(this.data);
    }

    /**
     * Capture state for undo before a batch of changes.
     * Debounced: the first trackChange in a batch saves the pre-change state,
     * subsequent calls within UNDO_BATCH_DELAY are grouped into the same undo entry.
     */
    private _captureUndoState() {
        if (!this._undoDebounceTimer) {
            if (this._snapshot) {
                this._undoStack.push(this._snapshot);
                if (this._undoStack.length > Spec.MAX_HISTORY) this._undoStack.shift();
                this._redoStack = [];
            }
        }
        if (this._undoDebounceTimer) clearTimeout(this._undoDebounceTimer);
        this._undoDebounceTimer = setTimeout(() => {
            this._undoDebounceTimer = null;
            this._snapshot = JSON.stringify(this.data);
        }, Spec.UNDO_BATCH_DELAY);
    }

    /**
     * Track a mutation for delta computation.
     */
    public trackChange(type: 'app' | 'flow' | 'meta', action: 'update' | 'create' | 'delete', id?: string, fields?: string[]) {
        if (this._isUndoRedo) return;
        this._captureUndoState();
        this._pendingChanges.push({ type, action, id, fields });
        if (this.onChange) this.onChange();
    }

    public canUndo(): boolean {
        return this._undoStack.length > 0;
    }

    public canRedo(): boolean {
        return this._redoStack.length > 0;
    }

    /**
     * Undo to the previous state.
     * Restores data in-place and returns true if successful.
     */
    public undo(): boolean {
        if (this._undoStack.length === 0) return false;
        this._isUndoRedo = true;
        if (this._undoDebounceTimer) { clearTimeout(this._undoDebounceTimer); this._undoDebounceTimer = null; }

        this._redoStack.push(JSON.stringify(this.data));
        if (this._redoStack.length > Spec.MAX_HISTORY) this._redoStack.shift();

        const prev = JSON.parse(this._undoStack.pop()!);
        this._restoreData(prev);

        this._snapshot = JSON.stringify(this.data);
        this._pendingChanges = [];
        this._isUndoRedo = false;
        return true;
    }

    /**
     * Redo to the next state.
     * Restores data in-place and returns true if successful.
     */
    public redo(): boolean {
        if (this._redoStack.length === 0) return false;
        this._isUndoRedo = true;
        if (this._undoDebounceTimer) { clearTimeout(this._undoDebounceTimer); this._undoDebounceTimer = null; }

        this._undoStack.push(JSON.stringify(this.data));
        if (this._undoStack.length > Spec.MAX_HISTORY) this._undoStack.shift();

        const next = JSON.parse(this._redoStack.pop()!);
        this._restoreData(next);

        this._snapshot = JSON.stringify(this.data);
        this._pendingChanges = [];
        this._isUndoRedo = false;
        return true;
    }

    /**
     * Restore data object in-place (preserves references).
     */
    private _restoreData(newData: any) {
        for (const key of Object.keys(this.data)) delete this.data[key];
        for (const key of Object.keys(newData)) this.data[key] = newData[key];
    }

    /**
     * Check if there are unsaved changes.
     */
    public isDirty(): boolean {
        return this._pendingChanges.length > 0;
    }

    /**
     * Build a delta object from pending changes.
     * Returns: { apps?: {id: fields|null}, flow?: {id: fields|null}, meta?: {fields} }
     */
    public getPendingDelta(): any {
        const delta: any = {};

        for (const change of this._pendingChanges) {
            if (change.type === 'app') {
                if (!delta.apps) delta.apps = {};
                if (change.action === 'delete') {
                    delta.apps[change.id!] = null;
                } else {
                    // For create/update, include the changed fields from current data
                    if (!delta.apps[change.id!]) delta.apps[change.id!] = {};
                    if (change.action === 'create') {
                        // Include full app data for new apps
                        delta.apps[change.id!] = this.data.apps[change.id!] ? { ...this.data.apps[change.id!] } : null;
                    } else if (change.fields && this.data.apps[change.id!]) {
                        for (const field of change.fields) {
                            delta.apps[change.id!][field] = this.data.apps[change.id!][field];
                        }
                    }
                }
            } else if (change.type === 'flow') {
                if (!delta.flow) delta.flow = {};
                if (change.action === 'delete') {
                    delta.flow[change.id!] = null;
                } else {
                    if (!delta.flow[change.id!]) delta.flow[change.id!] = {};
                    if (change.action === 'create') {
                        delta.flow[change.id!] = this.data.flow[change.id!] ? { ...this.data.flow[change.id!] } : null;
                    } else if (change.fields && this.data.flow[change.id!]) {
                        for (const field of change.fields) {
                            delta.flow[change.id!][field] = this.data.flow[change.id!][field];
                        }
                    }
                }
            } else if (change.type === 'meta') {
                if (!delta.meta) delta.meta = {};
                if (change.fields) {
                    for (const field of change.fields) {
                        delta.meta[field] = this.data[field];
                    }
                }
            }
        }

        return delta;
    }

    /**
     * Clear pending changes after successful save.
     */
    public clearPending() {
        this._pendingChanges = [];
    }

    /**
     * Apply a delta received from another source (e.g., collaboration).
     * Does NOT add to pending changes (avoids echo).
     */
    public applyDelta(delta: any) {
        if (delta.apps) {
            for (const appId in delta.apps) {
                if (delta.apps[appId] === null) {
                    delete this.data.apps[appId];
                } else if (typeof delta.apps[appId] === 'object') {
                    if (!this.data.apps[appId]) this.data.apps[appId] = {};
                    Object.assign(this.data.apps[appId], delta.apps[appId]);
                }
            }
        }
        if (delta.flow) {
            for (const flowId in delta.flow) {
                if (delta.flow[flowId] === null) {
                    delete this.data.flow[flowId];
                } else if (typeof delta.flow[flowId] === 'object') {
                    if (!this.data.flow[flowId]) this.data.flow[flowId] = {};
                    Object.assign(this.data.flow[flowId], delta.flow[flowId]);
                }
            }
        }
        if (delta.meta) {
            const reserved = new Set(['apps', 'flow']);
            for (const key in delta.meta) {
                if (!reserved.has(key)) {
                    if (delta.meta[key] === null) {
                        delete this.data[key];
                    } else {
                        this.data[key] = delta.meta[key];
                    }
                }
            }
        }
        this.takeSnapshot();
    }

    public app(app_id: any) {
        return new App(this, app_id);
    }

    public apps() {
        let apps = [];
        for (let app_id in this.data.apps) {
            apps.push(app_id);
        }
        return apps;
    }

    public deleteApp(app_id) {
        if (this.data.apps[app_id]) {
            delete this.data.apps[app_id];
            this.trackChange('app', 'delete', app_id);
        }
    }

    public createApp(data: any = {}) {
        let wpdata = this.data;

        let spec = {
            id: '',
            title: 'new app',
            version: '1.0.0',
            description: '',
            cdn: { js: [], css: [] },
            inputs: [],
            outputs: [],
            code: '',
            api: '',
            html: '',
            js: '',
            css: ''
        };

        for (let key in data) {
            spec[key] = data[key];
        }

        let app_id = this.random();
        while (wpdata.apps[app_id])
            app_id = this.random();
        spec.id = app_id;

        wpdata.apps[app_id] = spec;
        this.trackChange('app', 'create', app_id);
        return this.app(app_id);
    }

    public flow(flow_id: any) {
        return new Flow(this, flow_id);
    }

    public flows(app_id: any) {
        let flows = [];
        if (!app_id) {
            for (let flow_id in this.data.flow) {
                flows.push(flow_id);
            }
        } else {
            for (let flow_id in this.data.flow) {
                let flow = this.flow(flow_id)
                if (flow.app().id() == app_id)
                    flows.push(flow);
            }
        }
        return flows;
    }

    public deleteFlow(flow_id) {
        if (this.data.flow[flow_id]) {
            delete this.data.flow[flow_id];
            this.trackChange('flow', 'delete', flow_id);
        }
    }

    public createFlow(app_id: string, opt: any = {}) {
        let wpdata = this.data;
        let flow_id = app_id + "-" + new Date().getTime();
        while (wpdata.flow[flow_id])
            flow_id = app_id + "-" + new Date().getTime();

        let spec = {
            app_id: app_id,
            'class': '',
            data: {},
            active: true,
            description: '',
            id: flow_id,
            inputs: {},
            name: '',
            outputs: {},
            pos_x: opt.x,
            pos_y: opt.y,
            typenode: false
        };

        wpdata.flow[flow_id] = spec;
        this.trackChange('flow', 'create', flow_id);
        let flow = this.flow(flow_id);
        return flow;
    }

    public random(stringLength: number = 16) {
        const fchars = 'abcdefghiklmnopqrstuvwxyz';
        const chars = '0123456789abcdefghiklmnopqrstuvwxyz';
        let randomstring = '';
        for (let i = 0; i < stringLength; i++) {
            let rnum = null;
            if (i === 0) {
                rnum = Math.floor(Math.random() * fchars.length);
                randomstring += fchars.substring(rnum, rnum + 1);
            } else {
                rnum = Math.floor(Math.random() * chars.length);
                randomstring += chars.substring(rnum, rnum + 1);
            }
        }
        return randomstring;
    }
}

export default Spec;